import { PERCEPTION_MODELS } from 'constants/routePaths';

import * as React from 'react';

import { ArrowBack } from '@mui/icons-material';
import { Alert, Button, Stack } from '@mui/material';
import { useHistory } from 'react-router-dom';

import PerceptionDataExplorer from './PerceptionDataExplorer';
import PerceptionModelError from './PerceptionModelError';
import PerceptionModelPicker from './PerceptionModelPicker';
import usePerceptionModels from './usePerceptionModels';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * The perception data on its own, for looking rather than building.
 *
 * No anchors and no norm set, so nothing is addable and there is no reference
 * profile to compare against — each product is read on its own terms.
 */

const ExplorePage: React.FC = () => {
  const history = useHistory();
  const [jobId, setJobId] = React.useState<number | undefined>();
  const { allJobs, models, loading, error } = usePerceptionModels();

  const job = models.find((j) => j.id === jobId) ?? models[0];

  if (loading) return <PagePaper>Loading perception models…</PagePaper>;

  if (error) {
    return (
      <PagePaper>
        <PerceptionModelError error={error} />
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      {/* One level up. Every page in this flow answers "how do I get back"
          the same way. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(PERCEPTION_MODELS)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Norm sets
      </Button>

      <PageHeader>Perception models</PageHeader>
      <PageTitle>Perception data explorer</PageTitle>

      {!job && (
        <Alert severity="warning">
          No perception model has exported any products yet, so there is no
          space to explore.
        </Alert>
      )}

      {job && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <PerceptionModelPicker
            jobs={models}
            hidden={allJobs.length - models.length}
            value={job.id}
            onChange={setJobId}
          />

          {/* Keyed on the version: the selected product belongs to one
              model's universe, and carrying it across would leave the panel
              waiting on a row that does not exist in the new one. */}
          <PerceptionDataExplorer key={job.id} jobId={job.id} />
        </Stack>
      )}
    </PagePaper>
  );
};

export default ExplorePage;
